
import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from "@angular/core";

import Chart from "chart.js/auto";
import { EUserGender } from "src/app/entities/user.entity";
import {
  GROWTH_CHART_PROVIDERS_TOKEN,
  GrowthChartProviderValue,
} from "../growth-chart.module";

import {
  ageInMonths,
  formatAgeCalendar,
  formatAgeMonths,
  sameDay,
} from "src/app/common/utils/date.utils";

export enum GrowthChartMode {
  HEIGHT,
  WEIGHT,
}

/** Point affiché sur la courbe de l'enfant */
interface PatientPoint {
  x: number;
  y: number;
  ageLabel: string;
}

/** Couleurs de la courbe du patient */
const PATIENT_LINE_COLOR = "#2563eb"; // Bleu
const PATIENT_POINT_COLOR = "#e11d48"; // Rose
const OUTLIER_COLOR = "#f59e0b"; // Orange

@Component({
  selector: "app-growth-chart",
  templateUrl: "./growth-chart.component.html",
  styleUrls: ["./growth-chart.component.css"],
})
export class GrowthChartComponent
  implements AfterViewInit, OnChanges, OnDestroy {

  @Input() gender: EUserGender = EUserGender.HOMME;
  @Input() height: number = 0;
  @Input() weight: number = 0;

  /** Âge en mois entiers (fallback si birthDate absent) */
  @Input() old: number = 0;

  /** Date de naissance */
  @Input() birthDate: Date | string | number | null = null;

  /** Date de la consultation courante */
  @Input() consultationDate: Date | string | number | null = null;

  @Input() filter: any[] = [];
  @Input() mode: GrowthChartMode = GrowthChartMode.HEIGHT;
  @Input() date: number = 0;

  /** Poids à la naissance */
  @Input() poidsNassicne: number = 0;

  /** Taille à la naissance */
  @Input() tailleNassiance: number = 0;

  @ViewChild("canvas") canvas!: ElementRef;

  private chart?: Chart;
  private chartInitialized = false;

  constructor(
    private ngZone: NgZone,
    @Inject("GROWTH_CHART_PROVIDERS_DATA")
    private GROWTH_CHART_PROVIDERS_DATA: Map<
      GROWTH_CHART_PROVIDERS_TOKEN,
      Array<GrowthChartProviderValue>
    >
  ) {}

  // ─── Âge ─────────────────────────────────────────────────────────────

  /** Âge exact en mois */
  get currentAgeExact(): number {
    if (this.birthDate) {
      return ageInMonths(this.birthDate, this.date || new Date());
    }

    return Number(this.old) || 0;
  }

  /** Âge en mois entiers */
  get currentAge(): number {
    return Math.floor(this.currentAgeExact);
  }

  /** Vrai si l'enfant est à la naissance */
  get isBirth(): boolean {
    return this.currentAgeExact <= 0;
  }

  /** Courbe 2–20 ans ou 0–36 mois */
  get isOlder(): boolean {
    return this.currentAge >= 36;
  }

  /** Calcule la position X et l'étiquette d'âge */
  private resolveAge(
    elm: any,
    isCurrent: boolean
  ): { x: number; ageLabel: string } {

    const consultDate = isCurrent
      ? this.date || new Date()
      : elm.dateConsultation;

    if (this.birthDate && consultDate) {
      const x = ageInMonths(this.birthDate, consultDate);

      if (x <= 0) {
        return { x: 0, ageLabel: "Naissance" };
      }

      return {
        x,
        ageLabel: formatAgeCalendar(this.birthDate, consultDate),
      };
    }

    // Fallback : âge stocké dans l'historique
    const x = isCurrent ? Number(this.old) : Number(elm.date);

    if (x <= 0) {
      return { x: 0, ageLabel: "Naissance" };
    }

    return {
      x,
      ageLabel: formatAgeMonths(x),
    };
  }

  // ─── Chart ───────────────────────────────────────────────────────────

  get canvasContext(): any {
    return this.canvas.nativeElement.getContext("2d");
  }

  get unit(): string {
    return this.mode === GrowthChartMode.HEIGHT ? "cm" : "kg";
  }

  /** Affiche le label à la fin de chaque courbe OMS */
  get endLabelPlugin(): any {
    return {
      id: "endLabelPlugin",

      afterDatasetsDraw(chart: any) {
        const { ctx } = chart;

        chart.data.datasets.forEach(
          (dataset: any, index: number) => {
            if (dataset.isPatient) return;

            const meta = chart.getDatasetMeta(index);

            if (meta.hidden || !meta.data.length) return;

            const last = meta.data[meta.data.length - 1];

            ctx.save();
            ctx.fillStyle = dataset.labelColor || dataset.borderColor;
            ctx.font = "12px Arial";
            ctx.fillText(dataset.label, last.x + 5, last.y + 4);
            ctx.restore();
          }
        );
      },
    };
  }

  /** Sélectionne les courbes OMS selon l'âge, le sexe et le mode */
  get providerToken(): GROWTH_CHART_PROVIDERS_TOKEN {
    const older = this.isOlder;

    if (this.mode === GrowthChartMode.HEIGHT) {
      if (this.gender === EUserGender.FEMME) {
        return older
          ? GROWTH_CHART_PROVIDERS_TOKEN.HEIGHT_20Y_OLD_FEMAL
          : GROWTH_CHART_PROVIDERS_TOKEN.HEIGHT_3Y_OLD_FEMAL;
      }

      return older
        ? GROWTH_CHART_PROVIDERS_TOKEN.HEIGHT_20Y_OLD_MALE
        : GROWTH_CHART_PROVIDERS_TOKEN.HEIGHT_3Y_OLD_MALE;
    }

    if (this.gender === EUserGender.FEMME) {
      return older
        ? GROWTH_CHART_PROVIDERS_TOKEN.WEIGHT_20Y_OLD_FEMAL
        : GROWTH_CHART_PROVIDERS_TOKEN.WEIGHT_3Y_OLD_FEMAL;
    }

    return older
      ? GROWTH_CHART_PROVIDERS_TOKEN.WEIGHT_20Y_OLD_MALE
      : GROWTH_CHART_PROVIDERS_TOKEN.WEIGHT_3Y_OLD_MALE;
  }

  get xMin(): number {
    return this.isOlder ? 24 : 0;
  }

  get xMax(): number {
    return this.isOlder ? 240 : 36;
  }

  /** Construit les points des mesures de l'enfant */
  get plots(): PatientPoint[] {
    const getValue = (
      elm: any,
      isCurrent: boolean
    ): number | null => {

      const age = isCurrent
        ? this.currentAgeExact
        : Number(elm.date);

      // À la naissance
      if (age <= 0) {
        const value =
          this.mode === GrowthChartMode.HEIGHT
            ? Number(this.tailleNassiance)
            : Number(this.poidsNassicne);

        return value > 0 ? value : null;
      }

      if (this.mode === GrowthChartMode.HEIGHT) {
        if (!this.isValidHeight && isCurrent) return null;

        const value = Number(
          isCurrent ? this.height : elm.taille
        );

        return value > 0 ? value : null;
      }

      if (!this.isValidWeight && isCurrent) return null;

      const value = Number(
        isCurrent ? this.weight : elm.poids
      );

      return value > 0 ? value : null;
    };

    let history: any[] = [...(this.filter || [])];

    // Ajouter le point de naissance si nécessaire
    const hasBirthPoint = history.some((elm) => {
      const age =
        elm.dateConsultation && this.birthDate
          ? ageInMonths(this.birthDate, elm.dateConsultation)
          : Number(elm.date);

      return age <= 0;
    });

    if (!hasBirthPoint) {
      const birthValue =
        this.mode === GrowthChartMode.HEIGHT
          ? Number(this.tailleNassiance)
          : Number(this.poidsNassicne);

      if (birthValue > 0) {
        history = [
          {
            taille: Number(this.tailleNassiance),
            poids: Number(this.poidsNassicne),
            date: 0,
            dateConsultation: this.birthDate,
          },
          ...history,
        ];
      }
    }

    // Ajouter la mesure courante si elle n'existe pas déjà
    const existsInHistory = history.some((elm) =>
      sameDay(elm.dateConsultation, this.date)
    );

    if (
      !existsInHistory &&
      (this.isValidHeight || this.isValidWeight)
    ) {
      history = [
        ...history,
        {
          taille: Number(this.height),
          poids: Number(this.weight),
          date: this.old,
          dateConsultation: this.date,
        },
      ];
    }

    const points = history
      .map((elm) => {
        const isCurrent = sameDay(
          elm.dateConsultation,
          this.date
        );

        const y = getValue(elm, isCurrent);

        if (y === null) return null;

        const { x, ageLabel } = this.resolveAge(
          elm,
          isCurrent
        );

        return { x, y, ageLabel } as PatientPoint;
      })
      .filter(
        (p): p is PatientPoint =>
          p !== null &&
          p.x >= this.xMin &&
          p.x <= this.xMax
      )
      .sort((a, b) => a.x - b.x);

    // Éviter les doublons au même âge
    return points.filter(
      (p, i) =>
        i === points.length - 1 ||
        points[i + 1].x !== p.x
    );
  }

  // ─── Données et couleurs du graphique ────────────────────────────────

  get data(): any {
    const offset = this.xMin;

    // Couleurs originales des courbes de percentiles OMS
    const colors = [
      "#f7e5ee",
      "#f7e5ee",
      "#f7e5ee",
      "#a8faf5",
      "#46d5cd",
      "#a8faf5",
      "#f7e5ee",
      "#f7e5ee",
      "#f7e5ee",
    ];

    return {
      datasets: [
        // Courbes OMS
        ...(this.GROWTH_CHART_PROVIDERS_DATA
          .get(this.providerToken)
          ?.map((e, i) => ({
            label: e.label,

            data: e.values
              .map((v: number, idx: number) => ({
                x: offset + idx,
                y: v,
              }))
              .filter((p: any) => p.x <= this.xMax),

            borderColor: colors[i % colors.length],
            backgroundColor: colors[i % colors.length],
            borderWidth: 1.5,
            fill: false,
            cubicInterpolationMode: "monotone",

            // Pas de points visibles sur les courbes OMS
            pointRadius: 0,
            pointHoverRadius: 0,
            pointHitRadius: 0,

            order: 1,
          })) || []),

        // Courbe du patient : ligne bleue, points roses
        {
          label:
            this.mode === GrowthChartMode.HEIGHT
              ? "Taille"
              : "Poids",

          isPatient: true,
          data: this.plots,

          // Relier les mesures
          showLine: true,
          cubicInterpolationMode: "monotone",

          // Couleur de la ligne : BLEU
          borderColor: PATIENT_LINE_COLOR,
          borderWidth: 2.5,

          // Couleur de base des points : ROSE
          backgroundColor: PATIENT_POINT_COLOR,

          // Points atypiques : ORANGE ; autres points : ROSE
          pointBackgroundColor: (ctx: any) => {
            const point = ctx.raw as PatientPoint | undefined;

            if (point && this.isOutlier(point)) {
              return OUTLIER_COLOR;
            }

            return PATIENT_POINT_COLOR;
          },

          pointBorderColor: "#ffffff",
          pointBorderWidth: 2,

          // Points atypiques plus grands
          pointRadius: (ctx: any) => {
            const point = ctx.raw as PatientPoint | undefined;

            return point && this.isOutlier(point) ? 7 : 5;
          },

          pointHoverRadius: 7,
          pointHitRadius: 8,
          pointStyle: "circle",

          fill: false,
          order: 0,
        },
      ],
    };
  }

  /** Valeur d'un percentile OMS à un âge donné */
  private refAt(
    curveIndex: number,
    x: number
  ): number | null {

    const curve =
      this.GROWTH_CHART_PROVIDERS_DATA
        .get(this.providerToken)?.[curveIndex];

    if (!curve) return null;

    const i = x - this.xMin;
    const i0 = Math.floor(i);
    const i1 = Math.min(
      i0 + 1,
      curve.values.length - 1
    );

    if (i0 < 0 || i0 >= curve.values.length) {
      return null;
    }

    return (
      curve.values[i0] +
      (curve.values[i1] - curve.values[i0]) * (i - i0)
    );
  }

  /** Vérifie si la mesure est atypique par rapport aux courbes extrêmes */
  isOutlier(p: PatientPoint): boolean {
    const curves =
      this.GROWTH_CHART_PROVIDERS_DATA.get(this.providerToken) || [];

    const low = this.refAt(0, p.x);
    const high = this.refAt(curves.length - 1, p.x);

    if (low == null || high == null) return false;

    return p.y < low * 0.85 || p.y > high * 1.15;
  }

  // ─── Cycle de vie ────────────────────────────────────────────────────

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      if (this.isValid) {
        this.fillChart();
      }

      this.chartInitialized = true;
    });
  }

  ngOnChanges(_changes: SimpleChanges): void {
    if (!this.chartInitialized || !this.canvas) return;

    this.ngZone.runOutsideAngular(() => {
      this.chart?.destroy();
      this.chart = undefined;

      if (this.isValid) {
        this.fillChart();
      }
    });
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }

  /** Initialise le graphique */
  fillChart(): void {
    const older = this.isOlder;
    const isHeight = this.mode === GrowthChartMode.HEIGHT;
    const unit = this.unit;

    const config: any = {
      type: "line",

      data: this.data,

      options: {
        responsive: true,
        parsing: false,

        layout: {
          padding: {
            right: 80,
          },
        },

        interaction: {
          mode: "nearest",
          intersect: true,
        },

        scales: {
          x: {
            type: "linear",
            min: this.xMin,
            max: this.xMax,

            ticks: {
              stepSize: older ? 12 : 1,
              callback: (v: number) =>
                older ? `${v / 12} ans` : `${v}`,
            },

            title: {
              display: true,
              text: older ? "Âge (ans)" : "Âge (mois)",
            },
          },

          y: {
            min: older
              ? isHeight
                ? 75
                : 9
              : 0,

            max: older
              ? isHeight
                ? 195
                : 110
              : isHeight
                ? 106
                : 19,

            ticks: {
              stepSize: 1,
            },

            grid: {
              display: true,
              borderDash: [1, 1],
            },

            title: {
              display: true,
              text: isHeight
                ? "Taille (cm)"
                : "Poids (kg)",
            },
          },
        },

        plugins: {
          legend: {
            display: false,
          },

          tooltip: {
            enabled: true,

            // Afficher les informations du patient uniquement
            filter: (item: any) =>
              !!item.dataset.isPatient,

            callbacks: {
              title: (items: any[]) => {
                const raw = items[0]?.raw as
                  | PatientPoint
                  | undefined;

                return (
                  raw?.ageLabel ??
                  formatAgeMonths(items[0]?.parsed?.x ?? 0)
                );
              },

              label: (ctx: any) =>
                `${ctx.dataset.label} : ${ctx.parsed.y} ${unit}`,
            },
          },
        },
      },

      plugins: [this.endLabelPlugin],
    };

    this.chart = new Chart(this.canvasContext, config);
  }

  // ─── Validations ─────────────────────────────────────────────────────

  /** Affiche le canvas si une mesure est valide et l'âge correct */
  get isValid(): boolean {
    // Une mesure de naissance suffit pour afficher le graphique
    if (
      Number(this.poidsNassicne) > 0 ||
      Number(this.tailleNassiance) > 0
    ) {
      return true;
    }

    if (this.isBirth) return false;

    return (
      (this.isValidHeight || this.isValidWeight) &&
      this.isFitOld
    );
  }

  get isValidHeight(): boolean {
    return Number(this.height) > 0;
  }

  get isValidWeight(): boolean {
    return Number(this.weight) > 0;
  }

  get isFitOld(): boolean {
    return this.currentAge <= 216;
  }
}